import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Video, Film, Camera, Mic, Palette, Sparkles, Play, Users, MapPin, Package, Building, Home } from "lucide-react";
import SEO from "../components/SEO";
import axios from "axios";
import { API } from "../App";

const categoryIcons = {
  "Videographer": Video,
  "Cinematographer": Film,
  "Editor": Palette,
  "Camera Operator": Camera,
  "Sound Recordist": Mic,
  "Voice-over Artist": Mic,
  "VFX Artist": Sparkles,
  "Animator": Play,
  "DIT": Package,
  "Gaffer": Sparkles,
  "Grip": Package,
  "Model / Actor": Users,
  "Filming Location": MapPin,
  "Gear Rental": Package,
  "Production Company": Building,
  "Studio": Home,
  "Drone Operator": Camera,
  "Colorist": Palette,
  "Motion Graphics": Sparkles,
  "Producer": Users,
};

export default function Categories() {
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchCategories();
  }, []);

  const fetchCategories = async () => {
    try {
      const response = await axios.get(`${API}/categories`);
      setCategories(response.data);
    } catch (error) {
      console.error("Error fetching categories:", error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-background py-12 px-4 md:px-8" data-testid="categories-page">
      <SEO 
        url="/categories"
        title="Creative Categories"
        description="Browse creative professionals by category: videographers, cinematographers, editors, sound recordists, VFX artists, animators, and more in Cape Town."
        keywords={["videographer", "cinematographer", "editor", "sound recordist", "VFX artist", "animator", "drone operator"]}
      />
      
      <div className="max-w-7xl mx-auto">
        <div className="text-center mb-12">
          <h1 className="font-heading text-title uppercase">
            BROWSE BY <span className="text-orange-500">CATEGORY</span>
          </h1>
          <p className="text-zinc-500 mt-2">
            Find specialists across every creative field in film and video production
          </p>
        </div>

        {loading ? (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
            {[...Array(12)].map((_, i) => (
              <div key={i} className="h-40 bg-zinc-900 skeleton" />
            ))}
          </div>
        ) : (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
            {categories.map((category) => {
              const IconComponent = categoryIcons[category.name] || Video;
              return (
                <Link
                  key={category.category_id}
                  to={`/categories/${category.slug}`}
                  className="category-card group p-6 bg-zinc-900 border border-zinc-800 hover:border-orange-500/50 transition-all duration-300 text-left"
                  data-testid={`category-${category.slug}`}
                >
                  <IconComponent className="h-10 w-10 text-orange-500 mb-4 group-hover:scale-110 transition-transform" />
                  <h3 className="font-heading text-xl text-white mb-2">{category.name}</h3>
                  {category.description && (
                    <p className="text-zinc-500 text-sm mb-2 line-clamp-2">{category.description}</p>
                  )}
                  <p className="font-mono text-xs text-zinc-600 uppercase tracking-wider">
                    {category.profile_count || 0} profiles
                  </p>
                </Link>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
